// Skills — a sub-page of the Config section's Agents folder.
//
// Org-scoped CRUD against the unified /v1/skills API (DEV-16805/16810),
// with version history. Each row is materialized into a virtual
// `skills/<name>/SKILL.md` on each agent build (refinement + chat + PA
// surfaces). Migrated off theopolis's org_skill table (DEV-16962) — see
// lib/agent-skill.ts for the hook layer and the model differences.
//
// Versioning model (mirrors workflow/judge versioning elsewhere):
//   - Each content edit (description and/or body) cuts a new immutable
//     `SkillVersion` row with an author-supplied `comment` — REQUIRED
//     by the API when description or body is changed.
//   - Renaming the skill's slug (`name`) or its binding (appliesToAll /
//     bound agents) never cuts a version.
//   - `currentVersionId` on the parent points at the version agents see.
//     The "Versions" panel in the edit modal lets you view any past
//     version's content and "Restore" it (re-points the pointer).
import { fork } from '@cortex/utils/fork';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { AgentMultiSelect } from '../../components/common/AgentMultiSelect';
import {
  BindingTargetSelect,
  type IBindingTargets,
} from '../../components/common/BindingTargetSelect';
import { Modal } from '../../components/common/Modal';
import { PageHeader } from '../../components/common/PageHeader';
import { Table, type IColumn } from '../../components/common/Table';
import { Dropzone, type IDroppedFile } from '../../components/document-sets/Dropzone';
import '../../components/document-sets/document-sets.css';
import {
  bindingReachesAnyAgent,
  useCollectionIdsByAgent,
  useCollectionList,
} from '../../lib/agent-collection';
import { useAgentList } from '../../lib/agent-list';
import {
  useCreateSkill,
  useDeleteSkill,
  usePromoteSkillVersion,
  useSkillList,
  useSkillVersions,
  useUpdateSkillBindings,
  useUpdateSkillVersion,
  type CreateSkillInput,
  type SkillVersion,
  type SkillWithBindings,
  type UpdateSkillVersionInput,
} from '../../lib/agent-skill';
import { formatDateTime } from '../../lib/format';

export interface ISkillSearch {
  agentId?: string;
}

export const Route = createFileRoute('/agents/skills')({
  component: SkillsPage,
  validateSearch: (raw: Record<string, unknown>): ISkillSearch => ({
    agentId: typeof raw.agentId === 'string' ? raw.agentId : undefined,
  }),
});

function SkillsPage() {
  const skillsQuery = useSkillList();
  const remove = useDeleteSkill();
  const agentsQuery = useAgentList();
  const agentNameById = useMemo(
    () => new Map((agentsQuery.data ?? []).map((a) => [a.id, a.name])),
    [agentsQuery.data],
  );
  const collectionsQuery = useCollectionList();
  const collectionNameById = useMemo(
    () => new Map((collectionsQuery.data ?? []).map((c) => [c.id, c.name])),
    [collectionsQuery.data],
  );

  const initialSearch = Route.useSearch();

  const [editing, setEditing] = useState<SkillWithBindings | 'new' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<SkillWithBindings | null>(null);
  // List filters (DEV-17003 — parity with the memories page). An `?agentId=`
  // deep-link (agent-detail "View all") seeds the agent multi-select.
  const [search, setSearch] = useState('');
  const [agentFilterIds, setAgentFilterIds] = useState<string[]>(
    initialSearch.agentId ? [initialSearch.agentId] : [],
  );
  const [orgWideOnly, setOrgWideOnly] = useState(false);

  // Collection membership of the filtered agents, so the agent filter matches
  // everything that actually reaches those agents (appliesToAll ∪ direct ∪
  // collection-derived) — consistent with agent-detail's resolved counts.
  const collectionsByAgent = useCollectionIdsByAgent(agentFilterIds);

  const allSkills = skillsQuery.data ?? [];
  const skills = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allSkills.filter((s) => {
      if (orgWideOnly && !s.appliesToAll) return false;
      if (
        agentFilterIds.length > 0 &&
        !bindingReachesAnyAgent(s, agentFilterIds, collectionsByAgent)
      ) {
        return false;
      }
      if (
        q &&
        !s.name.toLowerCase().includes(q) &&
        !(s.currentVersion?.description ?? '').toLowerCase().includes(q)
      ) {
        return false;
      }
      return true;
    });
  }, [allSkills, search, agentFilterIds, orgWideOnly, collectionsByAgent]);

  const cols: IColumn<SkillWithBindings>[] = [
    {
      key: 'name',
      header: 'Name',
      cell: (s) => <code className="t-mono">{s.name}</code>,
      width: 220,
    },
    {
      key: 'description',
      header: 'Trigger (description)',
      cell: (s) => <span className="t-secondary">{s.currentVersion?.description ?? '—'}</span>,
    },
    {
      key: 'applies',
      header: 'Applies to',
      cell: (s) =>
        s.appliesToAll ? (
          <span className="badge badge--outline">All agents</span>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-1)' }}>
            {s.collectionIds.map((id) => (
              <span key={id} className="tag is-sky" title={id}>
                ◆ {collectionNameById.get(id) ?? id}
              </span>
            ))}
            {s.agentIds.map((id) => (
              <span
                key={id}
                className="badge badge--outline"
                title={id}
                style={{ fontFamily: 'var(--font-mono)' }}
              >
                {agentNameById.get(id) ?? id}
              </span>
            ))}
            {(s.nodeBindings ?? []).map((n) => (
              <span
                key={`${n.workflowId}|${n.nodeKey}`}
                className="tag is-plum"
                title={`Workflow ${n.workflowId} · node ${n.nodeKey}`}
              >
                ⬡ {n.nodeKey}
              </span>
            ))}
          </div>
        ),
      width: 220,
    },
    {
      key: 'version',
      header: 'Current version',
      cell: (s) => (
        <span className="t-mono" title={s.currentVersion?.comment}>
          v{s.currentVersion?.versionNumber ?? '—'}
        </span>
      ),
      width: 130,
    },
    {
      key: 'updated',
      header: 'Updated',
      cell: (s) => formatDateTime(s.updatedAt),
      width: 200,
    },
    {
      key: 'actions',
      header: '',
      cell: (s) => (
        <div style={{ display: 'flex', gap: 'var(--sp-1)', justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn--ghost btn--xs" onClick={() => setEditing(s)}>
            Edit
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--xs"
            onClick={() => setConfirmDelete(s)}
          >
            Delete
          </button>
        </div>
      ),
      width: 160,
    },
  ];

  return (
    <>
      <PageHeader
        title="Skills"
        actions={
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => setEditing('new')}
          >
            New skill
          </button>
        }
      />
      <div
        style={{
          display: 'flex',
          gap: 'var(--sp-2)',
          alignItems: 'center',
          flexWrap: 'wrap',
          marginBottom: 'var(--sp-2)',
        }}
      >
        <input
          type="search"
          className="input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search skills by name or trigger…"
          style={{ minWidth: '220px' }}
        />
        <div style={{ minWidth: '260px' }}>
          <AgentMultiSelect
            selectedIds={agentFilterIds}
            onChange={setAgentFilterIds}
            placeholder="Filter by bound agent…"
          />
        </div>
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--sp-1)',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={orgWideOnly}
            onChange={(e) => setOrgWideOnly(e.target.checked)}
          />
          <span className="t-xs">Org-wide</span>
        </label>
      </div>
      <Table<SkillWithBindings>
        rows={skills}
        columns={cols}
        rowKey={(s) => s.id}
        isLoading={skillsQuery.isLoading}
        error={(skillsQuery.error as Error | null) ?? null}
        emptyTitle={allSkills.length > 0 ? 'No skills match these filters' : 'No skills yet'}
        emptyDescription="Create a skill to give your agents a focused, auto-loadable instruction block."
      />
      <SkillModal
        editing={editing}
        onClose={() => setEditing(null)}
        onSaved={() => setEditing(null)}
      />
      <Modal
        open={confirmDelete !== null}
        onClose={() => {
          if (!remove.isPending) setConfirmDelete(null);
        }}
        eyebrow="DELETE SKILL"
        title={confirmDelete ? `Delete "${confirmDelete.name}"?` : ''}
        width="min(480px, 92vw)"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
            This skill and all of its historical versions will be removed from your organization and
            unloaded from agents on their next build. This cannot be undone.
          </p>
          {remove.error ? (
            <div className="field-error">{(remove.error as Error).message}</div>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setConfirmDelete(null)}
              disabled={remove.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--danger btn--sm"
              disabled={remove.isPending}
              onClick={() => {
                if (!confirmDelete) return;
                remove.mutate(confirmDelete.id, {
                  onSuccess: () => setConfirmDelete(null),
                });
              }}
            >
              {remove.isPending ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

// Mirrors SkillNameSchema in @cortex/types.
const skillNamePattern = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/**
 * Drop (or click to browse) a single .md/.txt file to replace the body
 * textarea's content (2026-07-10 scope addition). Reuses the document-sets
 * Dropzone's drag/drop + file-picker plumbing rather than inventing a new
 * upload widget — only the first dropped file is used (a skill has one body,
 * not a file tree), and content is read as text, not staged/uploaded
 * anywhere (the body is just local textarea state until Save).
 */
function SkillBodyUpload({ onUpload }: { onUpload: (text: string) => void }) {
  const [error, setError] = useState<string | null>(null);

  function handleFiles(files: IDroppedFile[]) {
    const first = files[0];
    if (!first) return;
    setError(null);
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') onUpload(reader.result);
    };
    reader.onerror = () => setError('Could not read that file.');
    reader.readAsText(first.file);
  }

  return (
    <div>
      <Dropzone
        onFiles={handleFiles}
        hint="Drop a .md file here, or click to browse — replaces the body above."
      >
        <span className="t-xs t-muted">Drop a .md file here, or click to browse</span>
      </Dropzone>
      {error ? (
        <span className="field-help" style={{ color: 'var(--status-danger)' }}>
          {error}
        </span>
      ) : null}
    </div>
  );
}

function SkillModal({
  editing,
  onClose,
  onSaved,
}: {
  editing: SkillWithBindings | 'new' | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const open = editing !== null;
  const isEdit = editing !== 'new' && editing !== null;
  const existing = isEdit ? (editing as SkillWithBindings) : null;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [body, setBody] = useState('');
  const [comment, setComment] = useState('');
  const [appliesToAll, setAppliesToAll] = useState(false);
  const [targets, setTargets] = useState<IBindingTargets>({
    agentIds: [],
    collectionIds: [],
    nodeBindings: [],
  });
  // Which (if any) historical version the user is previewing from the
  // "Versions" panel. The form above stays bound to the latest edit
  // intent; this state controls a read-only modal on top of it.
  const [previewVersion, setPreviewVersion] = useState<SkillVersion | null>(null);

  useEffect(() => {
    if (existing) {
      setName(existing.name);
      setDescription(existing.currentVersion?.description ?? '');
      setBody(existing.currentVersion?.body ?? '');
      setComment('');
      setAppliesToAll(existing.appliesToAll);
      setTargets({
        agentIds: existing.agentIds,
        collectionIds: existing.collectionIds,
        nodeBindings: existing.nodeBindings ?? [],
      });
      setPreviewVersion(null);
    } else if (editing === 'new') {
      setName('');
      setDescription('');
      setBody('');
      setComment('');
      setAppliesToAll(false);
      setTargets({ agentIds: [], collectionIds: [], nodeBindings: [] });
      setPreviewVersion(null);
    }
  }, [editing, existing]);

  // Did the user change description or body relative to the loaded
  // version? Only then is `comment` required.
  const isContentEdit = useMemo(() => {
    if (!existing) return false;
    return (
      description !== (existing.currentVersion?.description ?? '') ||
      body !== (existing.currentVersion?.body ?? '')
    );
  }, [existing, description, body]);

  const createSkill = useCreateSkill();
  const updateVersion = useUpdateSkillVersion();
  const updateBindings = useUpdateSkillBindings();

  const nodeKeys = (ns: { workflowId: string; nodeKey: string }[]) =>
    ns
      .map((n) => `${n.workflowId}|${n.nodeKey}`)
      .sort()
      .join(',');
  const bindingChanged =
    existing !== null &&
    (appliesToAll !== existing.appliesToAll ||
      targets.agentIds.slice().sort().join(',') !== [...existing.agentIds].sort().join(',') ||
      targets.collectionIds.slice().sort().join(',') !==
        [...existing.collectionIds].sort().join(',') ||
      nodeKeys(targets.nodeBindings) !== nodeKeys(existing.nodeBindings ?? []));

  async function onSubmit() {
    // Errors surface via the mutations' own `.error` state (read as
    // `saveError` below) — swallow here so a failed save doesn't become
    // an unhandled rejection at the fire-and-forget call site.
    try {
      await doSubmit();
    } catch {
      // handled via saveError
    }
  }

  async function doSubmit() {
    if (existing) {
      // Content edit and binding edit are separate endpoints in the
      // unified API — fire whichever changed. Renaming an existing
      // skill's slug is not supported server-side (create a new one).
      if (isContentEdit) {
        const patch: UpdateSkillVersionInput = { comment: comment.trim() };
        if (description.trim() !== (existing.currentVersion?.description ?? '')) {
          patch.description = description.trim();
        }
        if (body !== (existing.currentVersion?.body ?? '')) {
          patch.body = body;
        }
        await updateVersion.mutateAsync({ id: existing.id, patch });
      }
      if (bindingChanged) {
        await updateBindings.mutateAsync({
          id: existing.id,
          patch: {
            appliesToAll,
            agentIds: targets.agentIds,
            collectionIds: targets.collectionIds,
            nodeBindings: targets.nodeBindings,
          },
        });
      }
      onSaved();
      return;
    }
    const create: CreateSkillInput = {
      name: name.trim(),
      description: description.trim(),
      body,
      appliesToAll,
      agentIds: targets.agentIds,
      collectionIds: targets.collectionIds,
      nodeBindings: targets.nodeBindings,
      // Agent-side skill (this is the Agents ▸ Skills editor). The v2 doc-side
      // fields default to the agent shape; the workspace UI (#778) sets the
      // conversion/passthrough kinds when authoring doc skills.
      kind: 'agent',
      mimeMatches: [],
      triggerMode: null,
      engineKind: 'claude-prompt',
    };
    await createSkill.mutateAsync(create);
    onSaved();
  }

  const save = {
    isPending: createSkill.isPending || updateVersion.isPending || updateBindings.isPending,
  };
  const saveError = createSkill.error ?? updateVersion.error ?? updateBindings.error ?? null;

  const nameValid = skillNamePattern.test(name.trim());
  const commentValid = !isContentEdit || comment.trim().length > 0;
  const bindingValid =
    appliesToAll ||
    targets.agentIds.length > 0 ||
    targets.collectionIds.length > 0 ||
    targets.nodeBindings.length > 0;
  const canSubmit =
    nameValid &&
    description.trim().length > 0 &&
    body.trim().length > 0 &&
    commentValid &&
    bindingValid;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    fork(onSubmit());
  }

  return (
    <>
      <Modal
        open={open && previewVersion === null}
        onClose={onClose}
        eyebrow={
          existing ? `EDIT SKILL · v${existing.currentVersion?.versionNumber ?? '—'}` : 'NEW SKILL'
        }
        title={existing ? existing.name : 'New skill'}
        width="min(960px, 94vw)"
      >
        <form
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}
        >
          <label className="field">
            <span className="field-label">Name</span>
            <input
              type="text"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. concept-workflow"
              required={true}
              disabled={existing !== null}
            />
            <span className="field-help">
              {existing
                ? 'Renaming an existing skill is not supported — create a new skill instead.'
                : 'kebab-case (lowercase letters, digits, hyphens), unique per organization.'}
            </span>
            {name.length > 0 && !nameValid ? (
              <span className="field-error">Name must be kebab-case (e.g. concept-workflow).</span>
            ) : null}
          </label>

          <label className="field">
            <span className="field-label">Applies to</span>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-1)' }}>
              <input
                type="checkbox"
                checked={appliesToAll}
                onChange={(e) => setAppliesToAll(e.target.checked)}
              />
              <span className="t-xs">All agents in the org</span>
            </label>
            {!appliesToAll ? (
              <div style={{ marginTop: 'var(--sp-1)' }}>
                <BindingTargetSelect value={targets} onChange={setTargets} includeNodes={true} />
              </div>
            ) : null}
            {!bindingValid ? (
              <span className="field-error">
                A skill must bind to at least one agent or apply to all agents.
              </span>
            ) : null}
          </label>

          <label className="field">
            <span className="field-label">Trigger (description)</span>
            <textarea
              className="textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={500}
              required={true}
              placeholder="Single sentence describing WHEN this skill should auto-load. Mastra uses this as the trigger."
            />
            <span className="field-help">
              {description.length}/500. Be specific — this is what makes the skill auto-load.
            </span>
          </label>

          <label className="field">
            <span className="field-label">Body (markdown)</span>
            <textarea
              className="textarea"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={16}
              maxLength={20_000}
              required={true}
              placeholder={'# My skill\n\nWhat the agent should know once this skill is loaded.'}
              style={{
                fontFamily: 'var(--ff-mono, ui-monospace, SFMono-Regular, Menlo, monospace)',
                fontSize: 'var(--fs-sm)',
              }}
            />
            <span className="field-help">
              {body.length}/20000. No frontmatter — `name` and `description` are assembled
              server-side on read.
            </span>
          </label>

          <SkillBodyUpload onUpload={(text) => setBody(text.slice(0, 20_000))} />

          <label className="field">
            <span className="field-label">
              Change comment{' '}
              {existing && isContentEdit ? (
                <span style={{ color: 'var(--text-warning)' }}>· required</span>
              ) : null}
            </span>
            <input
              type="text"
              className="input"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={500}
              placeholder={
                existing
                  ? isContentEdit
                    ? 'What changed in this version? (required)'
                    : 'No change comment needed for binding-only edits.'
                  : 'Optional. Defaults to "Initial version" when blank.'
              }
              disabled={existing !== null && !isContentEdit}
            />
            <span className="field-help">
              {existing
                ? 'Captured on the new version row so future editors can see why the skill changed. Required when description or body change; ignored for binding-only edits.'
                : 'Stamped on the initial (v1) version. Optional — server defaults to "Initial version".'}
            </span>
            {existing && isContentEdit && !commentValid ? (
              <span className="field-error">A non-empty change comment is required.</span>
            ) : null}
          </label>

          {saveError ? <div className="field-error">{(saveError as Error).message}</div> : null}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
            <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary btn--sm"
              disabled={save.isPending || !canSubmit}
            >
              {save.isPending ? 'Saving…' : existing ? 'Save' : 'Create'}
            </button>
          </div>
        </form>

        {existing ? (
          <VersionsPanel skill={existing} onPreview={setPreviewVersion} onPromoted={onSaved} />
        ) : null}
      </Modal>

      <VersionPreviewModal version={previewVersion} onClose={() => setPreviewVersion(null)} />
    </>
  );
}

// ─── Versions panel inside the edit modal ────────────────────────────────

function VersionsPanel({
  skill,
  onPreview,
  onPromoted,
}: {
  skill: SkillWithBindings;
  onPreview: (v: SkillVersion) => void;
  onPromoted: () => void;
}) {
  const versionsQuery = useSkillVersions(skill.id);
  const promote = usePromoteSkillVersion(skill.id);

  const versions = versionsQuery.data ?? [];

  return (
    <div
      style={{
        marginTop: 'var(--sp-4)',
        paddingTop: 'var(--sp-4)',
        borderTop: '1px solid var(--border-subtle)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <span className="t-mono-label">VERSION HISTORY</span>
        <span className="t-secondary" style={{ fontSize: 'var(--fs-xs)' }}>
          {versions.length} version{versions.length === 1 ? '' : 's'}
        </span>
      </div>

      {versionsQuery.isLoading ? (
        <span className="t-secondary">Loading versions…</span>
      ) : versionsQuery.error ? (
        <span className="field-error">{(versionsQuery.error as Error).message}</span>
      ) : versions.length === 0 ? (
        <span className="t-secondary">No versions yet.</span>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-1)',
            maxHeight: 240,
            overflowY: 'auto',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-sm)',
          }}
        >
          {versions.map((v) => {
            const isCurrent = v.id === skill.currentVersionId;
            return (
              <div
                key={v.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '60px 1fr auto auto',
                  alignItems: 'center',
                  gap: 'var(--sp-2)',
                  padding: 'var(--sp-2) var(--sp-3)',
                  borderBottom: '1px solid var(--border-subtle)',
                  background: isCurrent ? 'var(--surface-2, transparent)' : undefined,
                }}
              >
                <span className="t-mono">
                  v{v.versionNumber}
                  {isCurrent ? (
                    <span
                      style={{
                        marginLeft: 'var(--sp-1)',
                        fontSize: 'var(--fs-xs)',
                        color: 'var(--text-success, var(--text-secondary))',
                      }}
                    >
                      ·current
                    </span>
                  ) : null}
                </span>
                <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                  <span
                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {v.comment}
                  </span>
                  <span className="t-secondary" style={{ fontSize: 'var(--fs-xs)' }}>
                    {formatDateTime(v.createdAt)}
                    {v.createdBySubject ? ` · ${v.createdBySubject}` : ''}
                  </span>
                </div>
                <button
                  type="button"
                  className="btn btn--ghost btn--xs"
                  onClick={() => onPreview(v)}
                >
                  View
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--xs"
                  disabled={isCurrent || promote.isPending}
                  title={
                    isCurrent ? 'Already current' : 'Re-point current_version_id at this version'
                  }
                  onClick={() => promote.mutate(v.id, { onSuccess: onPromoted })}
                >
                  {promote.isPending && promote.variables === v.id ? 'Restoring…' : 'Restore'}
                </button>
              </div>
            );
          })}
        </div>
      )}
      {promote.error ? (
        <span className="field-error">{(promote.error as Error).message}</span>
      ) : null}
    </div>
  );
}

// ─── Read-only preview of one historical version ─────────────────────────

function VersionPreviewModal({
  version,
  onClose,
}: {
  version: SkillVersion | null;
  onClose: () => void;
}) {
  return (
    <Modal
      open={version !== null}
      onClose={onClose}
      eyebrow={version ? `VERSION v${version.versionNumber}` : ''}
      title={version ? version.comment : ''}
      width="min(820px, 92vw)"
    >
      {version ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          <div className="t-secondary" style={{ fontSize: 'var(--fs-xs)' }}>
            Created {formatDateTime(version.createdAt)}
            {version.createdBySubject ? ` by ${version.createdBySubject}` : ''}
          </div>
          <label className="field">
            <span className="field-label">Trigger (description)</span>
            <textarea className="textarea" value={version.description} rows={3} readOnly={true} />
          </label>
          <label className="field">
            <span className="field-label">Body (markdown)</span>
            <textarea
              className="textarea"
              value={version.body}
              rows={16}
              readOnly={true}
              style={{
                fontFamily: 'var(--ff-mono, ui-monospace, SFMono-Regular, Menlo, monospace)',
                fontSize: 'var(--fs-sm)',
              }}
            />
          </label>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
